import React from "react";
import { Link } from "react-router-dom";
import { FileStack, LayoutTemplate, PhoneCall } from "lucide-react";
import { cn } from "@/lib/utils";
import { LEAD_CHANCE, LEAD_CHANNEL, STATUS_LABEL } from "@/lib/sk";

export const CHANCES = Object.entries(LEAD_CHANCE);
export const CHANNELS = Object.entries(LEAD_CHANNEL);

/** A few mutually exclusive choices as quiet pills – one tap, no dropdown. */
export function Choice({ value, onChange, options, testId, small }) {
  return (
    <div className="flex flex-wrap gap-1.5" role="radiogroup" data-testid={testId} data-value={value || ""}>
      {options.map(([k, label]) => (
        <button key={k} type="button" role="radio" aria-checked={value === k} onClick={() => onChange(k)} className={cn("inline-flex items-center rounded-full border font-medium transition-[background-color,border-color,color,transform] duration-150 active:scale-[0.97]", small ? "h-8 px-3 text-[12.5px]" : "h-10 px-4 text-[13.5px]", value === k ? "border-[hsl(var(--navy))] bg-[hsl(var(--navy))] text-white" : "border-black/[0.1] bg-white text-foreground/70 hover:border-black/[0.22] hover:text-foreground")} data-testid={`${testId}-${k}`}>{label}</button>
      ))}
    </div>
  );
}

/** Before a second case is made for the same thing: the open opportunity, or the work that already exists for the vessel. */
export function Lookalike({ data }) {
  const lead = data?.lead;
  const similar = data?.similar || [];
  if (!lead && !similar.length) return null;
  return (
    <div className="rounded-[14px] bg-[hsl(var(--v-assumed)/0.08)] px-4 py-3 text-[13.5px] leading-[1.55] text-foreground/85" data-testid="lead-lookalike">
      {lead ? <p>Det finnes allerede en åpen mulighet med {lead.why}: <Link to={`/p/${lead.id}`} className="font-semibold text-foreground underline-offset-[3px] hover:underline" data-testid="lead-lookalike-lead">#{lead.number} {lead.name}</Link>. Åpne den i stedet for å registrere en ny?</p> : null}
      {similar.length ? <p className={lead ? "mt-1.5" : ""}>Pågående arbeid for samme fartøy: {similar.map((s, i) => <span key={s.id}>{i ? ", " : ""}<Link to={`/p/${s.id}`} className="font-medium text-foreground underline-offset-[3px] hover:underline" data-testid={`lead-lookalike-${s.number}`}>#{s.number} {s.name}</Link> <span className="text-muted-foreground">({STATUS_LABEL[s.status] || s.status})</span></span>)}.</p> : null}
    </div>
  );
}

const STEPS = [
  [PhoneCall, "Nå", "Kunde, fartøy og hva det gjelder. Resten kan fylles inn senere."],
  [FileStack, "Når forespørselen kommer", "Legg dokumentene i den samme saken – eller start fra en LOS-mal. Da blir muligheten til arbeid."],
  [LayoutTemplate, "Kalkyle og tilbud", "Arbeidsomfang, kalkyle og tilbud i samme sak. Tilbudsoversikten følger med av seg selv."],
];

/** Why there is nothing to link later: the opportunity *is* the case, in its first phase. */
export function HowItContinues() {
  return (
    <aside className="rounded-[18px] bg-[hsl(210_20%_97.5%)] p-5" data-testid="lead-how">
      <div className="text-[12px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">Slik går det videre</div>
      <ol className="mt-4 space-y-5">
        {STEPS.map(([Icon, title, text], i) => (
          <li key={title} className="flex gap-3">
            <span className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-full", i === 0 ? "bg-[hsl(var(--navy))] text-white" : "bg-white text-muted-foreground shadow-[0_1px_2px_hsl(212_40%_16%/0.08)]")}><Icon className="h-3.5 w-3.5" strokeWidth={2} /></span>
            <span className="min-w-0"><span className="block text-[13.5px] font-semibold text-foreground">{title}</span><span className="mt-0.5 block text-[13px] leading-[1.5] text-muted-foreground">{text}</span></span>
          </li>
        ))}
      </ol>
    </aside>
  );
}
